import React, { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    Button,
    Form,
    Spinner,
} from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { extractErrorMessage } from '../../constants/common';
import instance from "../../services/baseService";
import DynamicCreateModal from '../common/DynamicCreateModal';
import DynamicModal from '../Employees/DynamicModal';
import { truncateTextOnly } from '../../utils';

const MonthlyRates = ({ clientId }) => {
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingRateId, setEditingRateId] = useState(null);
    const [monthlyRates, setMonthlyRates] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]);
    const [loadingServiceTypes, setLoadingServiceTypes] = useState(false);
    const [saving, setSaving] = useState(false);

    // Form data state
    const [formData, setFormData] = useState({
        service_type: '',
        amount: '',
        amount_type: '',
        billing_period: ''
    });

    // Validation errors
    const [validationErrors, setValidationErrors] = useState({});

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [creatingServiceType, setCreatingServiceType] = useState(false);
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        onConfirm: null,
        message: '',
    });

    // Predefined dropdown values
    const amountTypes = [
        { value: 'percentage', label: 'Percentage' },
        { value: 'fixed', label: 'Fixed' }
    ];

    const billingPeriods = [
        { value: 'monthly', label: 'Monthly' },
        { value: 'yearly', label: 'Yearly' },
        { value: 'incidental', label: 'Incidental' }
    ];

    // Service type creation modal configuration
    const serviceTypeCreateConfig = {
        name: 'Service Type',
        placeholder: 'Select Service Type',
        type: 'select',
        label: 'Service Type',
        apiEndpoint: '/api/v1/client/service-type/create',
        fields: [
            {
                key: 'name',
                label: 'Service Type Name',
                required: true,
                maxLength: 100,
                placeholder: 'Enter Service Type Name'
            }
        ]
    };

    useEffect(() => {
        fetchMonthlyRates();
        fetchServiceTypes();
    }, [clientId]);

    const fetchMonthlyRates = async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const response = await instance.post(`/api/v1/client/monthly-rates/list/${clientId}`);

            if (response.data?.type === "success") {
                setMonthlyRates(response.data?.data?.list);
            } else {
                setServiceTypes([]);
            }
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toast.error(errorMessage);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    const fetchServiceTypes = async () => {
        setLoadingServiceTypes(true);
        try {
            const response = await instance.get('/api/v1/client/service-type/list');

            if (response.data?.type === "success") {
                setServiceTypes(response.data?.data);
            } else {
                setServiceTypes([]);
            }
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toast.error(errorMessage);
            setLoadingServiceTypes(false);
        } finally {
            setLoadingServiceTypes(false);

        }
    };

    const validateAmount = (value) => {
        if (!value || value.trim() === '') {
            return 'Amount is required';
        }

        const amountRegex = /^\d+(\.\d{1,2})?$/;
        if (!amountRegex.test(value)) {
            return 'Please enter a valid amount (numbers and up to 2 decimal places only)';
        }

        const numValue = parseFloat(value);
        if (numValue <= 0) {
            return 'Amount must be greater than 0';
        }

        return null;
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.service_type) {
            errors.service_type = 'Service Type is required';
        }

        const amountError = validateAmount(formData.amount);
        if (amountError) {
            errors.amount = amountError;
        }

        if (!formData.amount_type) {
            errors.amount_type = 'Amount Type is required';
        }

        if (!formData.billing_period) {
            errors.billing_period = 'Billing Period is required';
        }

        return errors;
    };

    const handleInputChange = (field, value) => {
        // Special handling for amount field - only allow numbers and decimal
        if (field === 'amount') {
            // Allow empty string for deletion
            if (value === '') {
                setFormData(prev => ({ ...prev, [field]: value }));
            }
            // Only allow numbers and one decimal point
            else if (/^\d*\.?\d*$/.test(value)) {
                setFormData(prev => ({ ...prev, [field]: value }));
            }
            else {
                return;
            }
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const updatedErrors = { ...prev };
                delete updatedErrors[field];
                return updatedErrors;
            });
        }
    };

    const handleSave = async () => {
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }
        setSaving(true);
        try {
            const url = editingRateId
                ? `/api/v1/client/monthly-rates/update/${clientId}/${editingRateId}`
                : `/api/v1/client/monthly-rates/add/${clientId}`;

            const response = await instance.post(url, { ...formData });
            const savedRate = response.data?.data;
            setMonthlyRates(prev => {
                if (editingRateId) {
                    return prev.map(rate =>
                        rate.id === editingRateId ? savedRate : rate
                    );
                } else {
                    return [...prev, savedRate];
                }
            });
            toast.success(
                editingRateId
                    ? 'Monthly rate updated successfully'
                    : 'Monthly rate added successfully'
            );

            handleCancel();

        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (rate) => {
        setFormData({
            service_type: rate.service_type?.id || '',
            amount: rate.amount || '',
            amount_type: rate.amount_type || '',
            billing_period: rate.billing_period || ''
        });
        setEditingRateId(rate.id);
        setShowForm(true);
        setValidationErrors({});
    };

    const handleDelete = (rateId) => {
        handleCancel()
        setConfirmModal({
            show: true,
            message: "Are you sure you want to delete this monthly rate?",
            onConfirm: async () => {
                try {
                    const response = await instance.get(`/api/v1/client/monthly-rates/delete/${clientId}/${rateId}`);
                    if (response.data?.type === "success") {
                        setMonthlyRates(prev => prev.filter(rate => rate.id !== rateId));
                        toast.success('Monthly rate deleted successfully');
                    }
                } catch (error) {
                    const errorMessage = extractErrorMessage(error);
                    toast.error(errorMessage);
                }
            }
        });
    };

    const handleCancel = () => {
        setFormData({
            service_type: '',
            amount: '',
            amount_type: '',
            billing_period: ''
        });
        setShowForm(false);
        setEditingRateId(null);
        setValidationErrors({});
    };

    const handleCreateServiceType = async (formData) => {
        setCreatingServiceType(true);
        try {
            const response = await instance.post('/api/v1/client/service-type/create', formData);
            if (response.data?.type === "success") {
                setServiceTypes(prev => [...prev, response.data.data]);
                setFormData(prev => ({ ...prev, service_type: response.data?.data?.id }));

                // Clear validation error for service_type
                if (validationErrors.service_type) {
                    setValidationErrors(prev => {
                        const { service_type, ...rest } = prev;
                        return rest;
                    });
                }
                toast.success('Service type created successfully');
                setShowCreateModal(false);
            }
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toast.error(errorMessage);
            setCreatingServiceType(false);
        } finally {
            setCreatingServiceType(false);
        }
    };

    // Get display values for dropdowns
    const getAmountTypeLabel = (value) => {
        return amountTypes.find(type => type.value === value)?.label || value;
    };

    const getBillingPeriodLabel = (value) => {
        return billingPeriods.find(period => period.value === value)?.label || value;
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <div className="text-center">
                    <Spinner animation="border" role="status" className="mb-3">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="text-muted">Loading monthly rates...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="mb-5">
            {/* Form for creating/editing monthly rates */}
            {showForm && (
                <Card className="mb-3">
                    <Card.Body>
                        <h6 className="mb-3 text-primary">
                            {editingRateId ? 'Edit Monthly Rate' : 'Add New Monthly Rate'}
                        </h6>
                        <Row className="align-items-end">
                            <Col xs={12} sm={6} lg={3} className="mb-3">
                                <Form.Label className="fw-medium">
                                    Service Type <span className="text-danger">*</span>
                                </Form.Label>
                                <div className="d-flex">
                                    <Form.Select
                                        value={formData.service_type}
                                        isInvalid={!!validationErrors.service_type}
                                        onChange={(e) => handleInputChange('service_type', e.target.value)}
                                    >
                                        <option value="">
                                            {loadingServiceTypes ? 'Loading...' : 'Select Service Type...'}
                                        </option>
                                        {Array.isArray(serviceTypes) && serviceTypes.length > 0 ? (
                                            serviceTypes.map(type => (
                                                <option key={type.id} value={type.id}>
                                                    {truncateTextOnly(type.name, 50)}
                                                </option>
                                            ))
                                        ) : !loadingServiceTypes ? (
                                            <option value="" disabled>
                                                No Service Types Found
                                            </option>
                                        ) : null}
                                    </Form.Select>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        className='no-hover ml-2'
                                        onClick={() => setShowCreateModal(true)}
                                    >
                                        <FaPlus />
                                    </Button>
                                </div>
                                <div style={{ height: '20px', fontSize: '0.875rem' }} className="text-danger">
                                    {validationErrors.service_type}
                                </div>
                            </Col>

                            {/* Amount */}
                            <Col xs={12} sm={6} lg={3} className="mb-3">
                                <Form.Label className="fw-medium">
                                    Amount <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter amount"
                                    value={formData.amount}
                                    onChange={(e) => handleInputChange('amount', e.target.value)}
                                    isInvalid={!!validationErrors.amount}
                                />
                                <div style={{ height: '20px', fontSize: '0.875rem' }} className="text-danger">
                                    {validationErrors.amount}
                                </div>
                            </Col>

                            {/* Amount Type */}
                            <Col xs={12} sm={6} lg={3} className="mb-3">
                                <Form.Label className="fw-medium">
                                    Amount Type <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    value={formData.amount_type}
                                    isInvalid={!!validationErrors.amount_type}
                                    onChange={(e) => handleInputChange('amount_type', e.target.value)}
                                >
                                    <option value="">Select Amount Type...</option>
                                    {amountTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <div style={{ height: '20px', fontSize: '0.875rem' }} className="text-danger">
                                    {validationErrors.amount_type}
                                </div>
                            </Col>

                            {/* Billing Period */}
                            <Col xs={12} sm={6} lg={3} className="mb-3">
                                <Form.Label className="fw-medium">
                                    Billing Period <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    value={formData.billing_period}
                                    isInvalid={!!validationErrors.billing_period}
                                    onChange={(e) => handleInputChange('billing_period', e.target.value)}
                                >
                                    <option value="">Select Billing Period...</option>
                                    {billingPeriods.map(period => (
                                        <option key={period.value} value={period.value}>
                                            {period.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <div style={{ height: '20px', fontSize: '0.875rem' }} className="text-danger">
                                    {validationErrors.billing_period}
                                </div>
                            </Col>

                            {/* Action Buttons */}
                            <Col xs={12} className="mb-2 d-flex gap-2">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className='no-hover fw-bold'
                                    onClick={handleSave}
                                    disabled={saving}
                                    style={{ width: '100px', fontWeight: 'bold' }}
                                >
                                    {saving ? 'Saving...' : (editingRateId ? 'Update' : 'Save')}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className='no-hover fw-bold'
                                    onClick={handleCancel}
                                    disabled={saving}
                                    style={{ width: '100px', fontWeight: 'bold' }}
                                >
                                    Cancel
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* List of existing monthly rates */}
            {monthlyRates.length > 0 && (
                monthlyRates.map((rate, index) => (
                    <Card key={rate.id} className="mb-3">
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="mb-0 text-primary">Monthly Rate: {index + 1}</h6>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => handleEdit(rate)}
                                        className="d-flex align-items-center no-hover"
                                    >
                                        <FaEdit className="me-1" />
                                        <span>Edit</span>
                                    </Button>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDelete(rate.id)}
                                        className="d-flex align-items-center no-hover"
                                    >
                                        <FaTrash className="me-1" />
                                        <span>Delete</span>
                                    </Button>
                                </div>
                            </div>
                            <Row>
                                <Col sm={3} className="mb-2">
                                    <p className="fw-medium mb-1">Service Type</p>
                                    <p className="text-muted border p-2 rounded mb-0 text-truncate">
                                        {rate.service_type?.name || 'N/A'}
                                    </p>
                                </Col>
                                <Col sm={3} className="mb-2">
                                    <p className="fw-medium mb-1">Amount</p>
                                    <p className="text-muted border p-2 rounded mb-0">
                                        {rate.amount || 'N/A'}
                                    </p>
                                </Col>
                                <Col sm={3} className="mb-2">
                                    <p className="fw-medium mb-1">Amount Type</p>
                                    <p className="text-muted border p-2 rounded mb-0">
                                        {getAmountTypeLabel(rate.amount_type)}
                                    </p>
                                </Col>
                                <Col sm={3} className="mb-2">
                                    <p className="fw-medium mb-1">Billing Period</p>
                                    <p className="text-muted border p-2 rounded mb-0">
                                        {getBillingPeriodLabel(rate.billing_period)}
                                    </p>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                ))
            )}

            {!showForm && (
                <div className="text-center py-4">
                    <Button
                        variant="outline-primary"
                        onClick={() => setShowForm(true)}
                        className="d-inline-flex align-items-center no-hover justify-content-center"
                    >
                        <FaPlus className="me-2" /> Add Monthly Rate
                    </Button>
                </div>
            )}

            <DynamicCreateModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                config={serviceTypeCreateConfig}
                onSubmit={handleCreateServiceType}
                isSubmitting={creatingServiceType}
            />

            <DynamicModal
                show={confirmModal.show}
                onHide={() => setConfirmModal({ ...confirmModal, show: false })}
                title="Confirm Delete"
                buttons={[
                    {
                        label: 'Cancel',
                        variant: 'secondary',
                        action: 'custom',
                        onClick: () => setConfirmModal({ ...confirmModal, show: false })
                    },
                    {
                        label: 'Delete',
                        variant: 'danger',
                        action: 'custom',
                        onClick: async () => {
                            setConfirmModal({ ...confirmModal, show: false });
                            if (confirmModal.onConfirm) await confirmModal.onConfirm();
                        }
                    }
                ]}
            >
                <p>{confirmModal.message}</p>
            </DynamicModal>
        </div>
    );
};

export default MonthlyRates;